'use client'

import React, { useState, useEffect, useRef } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import { getGraphData } from './utils'
import { formatMalwareCategoryLabel } from './malwareCategoryLabels'
import { PieGraphApiResponse, PieDataPoint } from './MalwareTrends.interface'

// Custom hook to load Highcharts modules (includes 'highcharts-more' and 'item-series')
function useHighchartsModules() {
  const modulesLoaded = useRef(false)

  useEffect(() => {
    if (modulesLoaded.current) return

    // Dynamically import modules
    import('highcharts/highcharts-more')
    import('highcharts/modules/item-series')

    modulesLoaded.current = true
  }, [])
}

// color palette for the chart categories
const HIGHCHARTS_COLORS = [
  '#a6cee3',
  '#1f78b4',
  '#b2df8a',
  '#33a02c',
  '#fb9a99',
  '#e31a1c',
  '#fdbf6f',
  '#ff7f00',
  '#cab2d6',
  '#6a3d9a',
  '#ffff99',
  '#b15928',
]

/**
 * Utility function updated to process data for a SINGLE 'item' series
 * with multiple data points, including color and label.
 */
const processDataForItemChart = (apiData: PieGraphApiResponse): Highcharts.PointOptionsObject[] => {
  // Returns an array of data points
  if (!apiData || !apiData.data) return []

  const ITEM_MULTIPLIER = 2

  return apiData.data.map((point: PieDataPoint, index: number) => {
    const name = formatMalwareCategoryLabel(point[0])
    const value = Math.round(point[1]) * ITEM_MULTIPLIER
    const color = HIGHCHARTS_COLORS[index % HIGHCHARTS_COLORS.length]
    const label = name // Use the name as the label

    return {
      name: name,
      y: value,
      color: color,
      label: label,
    } as Highcharts.PointOptionsObject // Cast to ensure Highcharts recognizes 'label'
  })
}

/**
 * Main component to render the Malware Category visualization.
 */
const GraphMalwareCategory: React.FC = () => {
  useHighchartsModules()

  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const apiData = (await getGraphData('pie')) as PieGraphApiResponse
        const highchartsDataPoints = processDataForItemChart(apiData)

        setChartOptions({
          chart: {
            type: 'item',
            style: {
              fontFamily: 'var(--gt-haptik-font-family)',
            },
          },
          title: {
            text: 'Malware',
            style: {
              color: '#FFFFFF',
            },
          },
          subtitle: {
            text: 'category distribution',
            style: {
              color: '#FFFFFF',
            },
          },
          legend: {
            enabled: true,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            // custom labelFormatter needed -- standard parliament initializer doesn't show legend colors
            labelFormatter: function () {
              const point = this as Highcharts.Point
              const color = point.color || 'gray'
              const name = point.name || 'Unknown'
              const yValue = point.y || 0
              return `
                    <span class="legend-wrapper hover:opacity-75">
                      <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; margin-right: 5px; vertical-align: bottom;"></span>
                      <span style="color: #FFFFFF; font-weight: bold;">${name}</span> 
                      <span style="opacity: 0.6; font-weight: normal; color: #FFFFFF;">${yValue}</span>
                    </span>
                `
            },
            useHTML: true,
            symbolRadius: 6, // Ensure legend symbols are circles
            symbolWidth: 12,
            symbolHeight: 12,
            itemStyle: {
              color: '#FFFFFF',
            },
            itemHoverStyle: {
              color: '#b0b0b0',
            },
          },
          series: [
            {
              type: 'item',
              name: 'Malware Categories',
              keys: ['name', 'y', 'color', 'label'],
              data: highchartsDataPoints,
              dataLabels: {
                enabled: true,
                format: '{point.label}',
                style: {
                  color: 'white',
                  textOutline: '1px contrast',
                },
              },
              marker: {
                radius: 6,
              },
              center: ['50%', '88%'],
              size: '170%',
              startAngle: -100,
              endAngle: 100,
            },
          ],
          credits: {
            enabled: false,
          },
          tooltip: {
            pointFormat:
              '<span style="color:{point.color}">●</span> {point.name}: <b>{point.y} ({point.percentage:.1f}%)</b><br/>',
          },
          responsive: {
            rules: [
              {
                condition: {
                  maxWidth: 600,
                },
                chartOptions: {
                  series: [
                    {
                      type: 'item',
                      dataLabels: {},
                      marker: {
                        radius: 5,
                      },
                      size: '100%',
                    },
                  ],
                },
              },
              {
                condition: {
                  minWidth: 601,
                },
                chartOptions: {
                  chart: {
                    width: null,
                  },
                },
              },
            ],
          },
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  if (isLoading) {
    return (
      <div className="w-full text-center py-[100px] text-white text-[16px]">
        Loading category data. Please wait...
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full text-center py-[100px] text-white text-[16px]">Error: {error}</div>
    )
  }

  if (!chartOptions) {
    return (
      <div className="w-full text-center py-[100px] text-white text-[16px]">No data available.</div>
    )
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}

export default GraphMalwareCategory
